import { PUBLIC_CAD_MONEY_MAX_CENTS } from '../../utils/publicCadMoney.js';

const INTENT_SET = new Set([
  'profitability_guide',
  'property_value',
  'property_sale',
  'legacy_owner_response',
]);

const UNIT_MIX_KEYS = [
  'oneAndHalf',
  'twoAndHalf',
  'threeAndHalf',
  'fourAndHalf',
  'fiveAndHalf',
  'sixAndHalfPlus',
  'commercial',
  'other',
];

const EXPENSE_KEYS_ALLOW_NULL = new Set([
  'municipalTaxesCents',
  'schoolTaxesCents',
  'insuranceCents',
  'heatingElectricityCents',
]);

const EXPENSE_KEYS = [
  'municipalTaxesCents',
  'schoolTaxesCents',
  'insuranceCents',
  'heatingElectricityCents',
  'otherExpensesCents',
];

const UNIT_COUNT_MAX = 9999;

function invalid() {
  return new TypeError('Invalid admin public response DTO');
}

function parseCents(value, { allowNull = false } = {}) {
  if (value === null) {
    if (allowNull) return null;
    throw invalid();
  }
  if (!Number.isInteger(value) || value < 0 || value > PUBLIC_CAD_MONEY_MAX_CENTS) throw invalid();
  return value;
}

function requireBoolean(value) {
  if (typeof value !== 'boolean') throw invalid();
  return value;
}

function parseUnitCount(value) {
  if (!Number.isInteger(value) || value < 0 || value > UNIT_COUNT_MAX) throw invalid();
  return value;
}

function parseOptionalNonNegativeInt(value) {
  if (value === null) return null;
  if (!Number.isInteger(value) || value < 0) throw invalid();
  return value;
}

function parseV3FinancialSnapshot(snap) {
  if (!snap || typeof snap !== 'object' || Array.isArray(snap)) throw invalid();
  const expensesIn = snap.expenses;
  if (!expensesIn || typeof expensesIn !== 'object' || Array.isArray(expensesIn)) throw invalid();
  const expenses = {};
  for (const key of EXPENSE_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(expensesIn, key)) throw invalid();
    expenses[key] = parseCents(expensesIn[key], { allowNull: EXPENSE_KEYS_ALLOW_NULL.has(key) });
  }
  if (
    snap.otherExpensesDescription !== null &&
    typeof snap.otherExpensesDescription !== 'string'
  ) {
    throw invalid();
  }
  return {
    currency: snap.currency === 'CAD' ? 'CAD' : null,
    period: snap.period === 'annual_current' ? 'annual_current' : null,
    grossAnnualIncomeCents: parseCents(snap.grossAnnualIncomeCents, { allowNull: false }),
    expenses,
    otherExpensesDescription:
      typeof snap.otherExpensesDescription === 'string' ? snap.otherExpensesDescription : null,
  };
}

function parseV3PropertyProfile(profile) {
  if (!profile || typeof profile !== 'object' || Array.isArray(profile)) throw invalid();
  const unitMixIn = profile.unitMix;
  if (!unitMixIn || typeof unitMixIn !== 'object' || Array.isArray(unitMixIn)) throw invalid();
  const unitMix = {};
  for (const key of UNIT_MIX_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(unitMixIn, key)) throw invalid();
    unitMix[key] = parseUnitCount(unitMixIn[key]);
  }
  const constructionYearUnknown = requireBoolean(profile.constructionYearUnknown);
  const unitCountMismatchConfirmed = requireBoolean(profile.unitCountMismatchConfirmed);
  let constructionYear = profile.constructionYear;
  if (constructionYear === null) {
    if (!constructionYearUnknown) throw invalid();
  } else if (!Number.isInteger(constructionYear)) {
    throw invalid();
  }
  return {
    constructionYear: constructionYear === null ? null : constructionYear,
    constructionYearUnknown,
    unitMix,
    unitCountMismatchConfirmed,
    declaredUnitTotal: parseOptionalNonNegativeInt(profile.declaredUnitTotal),
    importedNumberOfUnits: parseOptionalNonNegativeInt(profile.importedNumberOfUnits),
  };
}

export function parseGetAdminProspectCampaignMailingPublicResponse(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw invalid();
  const source = Array.isArray(data.responses) ? data.responses : null;
  if (!source) throw invalid();
  const responses = source.map((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) throw invalid();
    if (!INTENT_SET.has(item.intent)) throw invalid();
    const json = JSON.stringify(item);
    if (/tokenHash|"token"|fingerprint|operationId|storagePath|publicResponses\//.test(json)) {
      throw invalid();
    }
    const contact = item.contact && typeof item.contact === 'object' ? item.contact : {};
    const out = {
      intent: item.intent,
      submittedAt: typeof item.submittedAt === 'string' || item.submittedAt == null ? item.submittedAt : null,
      contact: {
        fullName: typeof contact.fullName === 'string' ? contact.fullName : null,
        email: typeof contact.email === 'string' ? contact.email : null,
        phone: typeof contact.phone === 'string' ? contact.phone : null,
      },
      consentAccepted: item.consentAccepted === true,
    };
    if (item.intent === 'property_sale') out.ownershipAttested = item.ownershipAttested === true;
    if (typeof item.schemaVersion === 'number') out.schemaVersion = item.schemaVersion;
    if (item.language === 'fr' || item.language === 'en') out.language = item.language;

    if (item.intent === 'property_value' && item.schemaVersion === 3) {
      if (!item.financialSnapshot || !item.propertyProfile) throw invalid();
      out.financialSnapshot = parseV3FinancialSnapshot(item.financialSnapshot);
      out.propertyProfile = parseV3PropertyProfile(item.propertyProfile);
    }
    return out;
  });
  return { responses };
}

export function buildGetAdminProspectCampaignMailingPublicResponseRequest({ campaignId, mailingId }) {
  const c = typeof campaignId === 'string' ? campaignId.trim() : '';
  const m = typeof mailingId === 'string' ? mailingId.trim() : '';
  if (!c || !m) throw new TypeError('campaignId and mailingId are required');
  return { campaignId: c, mailingId: m };
}
